<template>
	<ja-modal
		v-if="show"
		class="add-student-sim-modal"
		:show="show"
		:simulation="simulation"
	>
		<div class="modal-header">
			<ja-close-button
				name="close"
				@click.native="closeModalAddStudentSim"
			></ja-close-button>

			<div class="title-wrapper">
				<h2 class="modal-title">{{ $t(modalTitleKey) }}</h2>
			</div>
			<div v-if="!isPreviewVisible">
				<div class="toggle-button-wrapper">
					<div
						class="btn-group btn-group--no-gap btn-group--center btn-group--eq"
					>
						<ja-button
							:name="studentsSources.UMS"
							class="small squared"
							:class="{
								'outlined lighter': isFPactiveFilter
							}"
							:disabled="canDisableUMSButton"
							@click.native="setFilter(studentsSources.UMS)"
						>
							{{ $t('ums') }}
						</ja-button>
						<ja-button
							:name="studentsSources.FINANCE_PARK"
							class="small squared"
							:class="{
								'outlined lighter': isUMSactiveFilter
							}"
							:disabled="canDisableFPButton"
							@click.native="setFilter(studentsSources.FINANCE_PARK)"
						>
							{{ $t('financePark') }}
						</ja-button>
					</div>
				</div>
				<div class="search-wrapper">
					<ja-input
						v-if="!isPreviewVisible"
						escape-string
						class="form-input"
						name="name"
						:value="searchValue"
						:placeholder="$t('searchByName')"
						autocomplete="off"
						@input="updateSearchValue($event)"
					></ja-input>
				</div>
			</div>
			<div
				v-if="usersHasSimulations"
				class="sim-info-text text--center text--muted"
			>
				<span>{{ $t('usersHasSimulationsMessage') }}</span>
			</div>
		</div>
		<perfect-scrollbar class="students-container">
			<div v-if="!isPreviewVisible">
				<ja-multiselectable-list
					:preselected-items="selectedStudents"
					:items="students"
					:item-options="{
						titleProperties: ['firstName', 'lastName'],
						subtitleProperties: ['emailAddress']
					}"
					:page="currentPage"
					@selectedItems="setSelectedStudents($event)"
				/>
			</div>
			<div v-else>
				<ja-toggable-card-list
					:items="selectedStudents"
					:item-options="{
						titleProperties: ['firstName', 'lastName'],
						subtitleProperties: ['emailAddress'],
						descriptionProperties: ['firstName'],
						checkForContentBy: 'simulations'
					}"
					@toggleSelect="updateSelectedItems($event)"
				>
					<template v-slot:infoDescription="{ item }">
						{{ $tc('participationCount', item.simulations.length) }}
					</template>
					<template v-slot:content="{ item }">
						<ul v-if="item.simulations.length" class="ul--style-none">
							<li v-for="sim in item.simulations" :key="sim.id">
								{{ sim.sessionName.name }}
								<svgicon
									class="icon--clickable icon--danger icon--large"
									name="alert-close"
									@click="confirmSimRemove(item, sim)"
								></svgicon>
							</li>
						</ul>
					</template>
				</ja-toggable-card-list>
			</div>
		</perfect-scrollbar>

		<div class="pagination-wrapper">
			<ja-pagination
				v-if="!isPreviewVisible"
				:data-per-page="studentsPerPage"
				:data-count="studentsCount"
				:current="currentPage"
				small
				@changePage="updatePagination"
			></ja-pagination>
		</div>
		<div class="btn-group btn-group--center mb--2 mt--2">
			<ja-button
				v-if="!isPreviewVisible"
				:disabled="selectedStudents.length === 0"
				name="next-btn"
				class="small"
				@click.native="gotoPreview()"
			>
				{{ $t('next') }}
			</ja-button>
			<ja-button
				v-if="isPreviewVisible"
				name="back-btn"
				class="small outlined"
				@click.native="backToSelection()"
			>
				{{ $t('back') }}
			</ja-button>
			<ja-button
				v-if="isPreviewVisible"
				:disabled="isAddDisabled"
				name="add-btn"
				class="small"
				@click.native="submitSelectionStudents()"
			>
				{{ $t('add') }}
			</ja-button>
		</div>
		<ja-confirm-modal
			class="deeper-level"
			:confirm-text="$t('yes')"
			:cancel-text="$t('no')"
			:show="isConfirmVisible"
			@cancel="cancelConfirmModal()"
			@closeModal="cancelConfirmModal()"
			@confirm="onConfirm()"
		>
			{{ $t(confirmMessage) }}
		</ja-confirm-modal>
	</ja-modal>
</template>

<script>
import gql from 'graphql-tag';
import Modal from '@/components/Modal/Modal';
import Input from '@/components/ui/form/Input';
import Pagination from '../elements/Pagination';
import _ from 'lodash';
import { getNameAndEmailFilter } from '@/helpers/queryFilters';
import { MultiSelectableList, Button, CloseButton } from '@ui';
import { studentsSources } from '@/helpers/constants';
import { ToggableCardList } from '@ui';
import StudentsService from '@/services/StudentsService';
import ConfirmModal from '@/components/Modal/ConfirmModal';

export default {
	components: {
		jaModal: Modal,
		jaButton: Button,
		jaCloseButton: CloseButton,
		jaInput: Input,
		jaPagination: Pagination,
		jaMultiselectableList: MultiSelectableList,
		jaToggableCardList: ToggableCardList,
		jaConfirmModal: ConfirmModal
	},
	props: {
		show: {
			type: Boolean,
			value: false
		},
		listStudents: {
			type: Array,
			default: null
		},
		simulation: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			selectedStudents: [],
			studentsForDeselection: [],
			students: [],
			searchValue: '',
			studentsCount: 0,
			studentsPerPage: 50,
			currentPage: 1,
			activeFilter: studentsSources.UMS,
			studentsSources: studentsSources,
			isPreviewVisible: false,
			isConfirmVisible: false,
			confirmMessage: 'studentAddSimRemovalConfirmation',
			simForRemove: null,
			modalTitleKey: 'addStudents',
			isUMSactiveFilter: true,
			isFPactiveFilter: false,
			canDisableUMSButton: false,
			canDisableFPButton: false
		};
	},
	computed: {
		usersHasSimulations() {
			return (
				this.isPreviewVisible &&
				this.selectedStudents.some(user => user.simulations.length > 0)
			);
		},
		isAddDisabled() {
			return (
				this.selectedStudents.length === 0 ||
				this.studentsForDeselection.length === this.selectedStudents.length
			);
		}
	},
	watch: {
		selectedStudents() {
			if (!this.selectedStudents.length) {
				this.isPreviewVisible = false;
			}
		},
		isPreviewVisible() {
			this.modalTitleKey = this.isPreviewVisible
				? 'confirmStudentsSelection'
				: 'addStudents';
		}
	},
	methods: {
		gotoPreview() {
			this.isPreviewVisible = true;
		},
		backToSelection() {
			// we need that in order to update selection
			// but only if there is something for deselect
			// no need of additional "empty" operations
			if (this.studentsForDeselection.length) {
				this.updateSelectedStudents();
			}

			this.isPreviewVisible = false;
		},
		closeModalAddStudentSim() {
			this.$emit('closeModalAddStudentSim');
			this.selectedStudents = [];
			this.searchValue = '';
			this.refetchSimulationStudentsCount();
		},
		updateSelectedStudents() {
			// get only those items from `selectedStudents`
			// that are not presented in the `studentsForDeselection`
			// a.k.a those that are not marked for NOT add to the sim.
			this.selectedStudents = _.differenceBy(
				this.selectedStudents,
				this.studentsForDeselection,
				'id'
			);
			// reset the selection since selectedStudents is updated
			// this will give us the opportunity to deselect again
			this.studentsForDeselection = [];
		},
		updateSelectedItems(data) {
			// mark items for deselect when clicking on back button
			// it is called when a selection is toggled on the second screen
			// of the modal.
			if (!data.isSelected) {
				this.studentsForDeselection.push(data);
			} else {
				this.studentsForDeselection = this.studentsForDeselection.filter(
					item => item.id !== data.id
				);
			}
		},
		disableFilterButton() {
			if (this.isUMSactiveFilter) {
				this.canDisableFPButton = true;
			} else {
				this.canDisableUMSButton = true;
			}
		},
		enableFilterButtons() {
			this.canDisableFPButton = false;
			this.canDisableUMSButton = false;
		},
		disableNonActiveFilter(selectedStudents) {
			if (selectedStudents.length > 0) {
				this.disableFilterButton();
			} else {
				this.enableFilterButtons();
			}
		},
		setSelectedStudents(event) {
			this.disableNonActiveFilter(event);
			this.selectedStudents = event;
		},
		removeSelectedStudent(item) {
			this.selectedStudents = this.selectedStudents.filter(user => {
				return user.id !== item.id;
			});

			this.isConfirmVisible = false;
			this.resetConfirmValues();
		},
		onConfirm() {
			if (this.simForRemove) {
				this.removeStudentFromSim();
			}
		},
		confirmSimRemove(student, simulation) {
			this.simForRemove = { student, simulation };

			this.isConfirmVisible = true;
		},
		cancelConfirmModal() {
			this.isConfirmVisible = false;
			this.resetConfirmValues();
		},
		resetConfirmValues() {
			this.simForRemove = null;
			this.confirmMessage = 'studentAddSimRemovalConfirmation';
		},
		removeStudentFromSim() {
			this.$store.commit('setLoading', true);

			const { emailAddress } = this.simForRemove.student;
			const { simId } = this.simForRemove.simulation;

			// get only ids by given user and simulation.
			StudentsService.getStudentsIdsByUserData({
				email: emailAddress,
				simulationId: simId
			}).then(({ data }) => {
				// remap the ids as the result is array of objects
				// but we need array of integers
				const ids = data.students.map(record => record.id);

				// Remove the records and refetch the information
				StudentsService.removeStudentsFromSimulation(ids).then(() => {
					this.refetchAndUpdateUsers();
				});
			});
		},
		refetchAndUpdateUsers() {
			// refetch the students query and intersect with already selected records.
			// that way we will update the selectedStudents to match
			// what was previously selected
			this.$apollo.queries.students.refetch().then(() => {
				// intersect is made by id so that way we
				// match only those records that persist in both arrays
				// with their id
				this.selectedStudents = _.intersectionBy(
					this.students,
					this.selectedStudents,
					'id'
				);

				this.resetConfirmValues();
				this.isConfirmVisible = false;
				this.$store.commit('setLoading', false);
			});
		},
		refetchSimulationStudentsCount() {
			if (this.$apollo.queries.allStudentsCount) {
				this.$apollo.queries.allStudentsCount.refetch();
			}
		},
		async submitSelectionStudents() {
			this.$store.commit('setLoading', true);
			// update the selection before proceed
			// this is needed in order to get correct data
			this.updateSelectedStudents();

			const list = [];

			for (let key in this.selectedStudents) {
				let student = this.selectedStudents[key];
				let studentObj = {};

				// studentObj.id = student.id;
				studentObj.simulationId = this.simulation.id;
				studentObj.firstName = student.firstName;
				studentObj.lastName = student.lastName;
				studentObj.email = student.emailAddress;
				studentObj.source = student.source;
				studentObj.userId = student.id;
				studentObj.lifeStage_ID = this.simulation.cmsUpperSimulationTemplate
					? this.simulation.cmsUpperSimulationTemplate.startLifeStage_ID
					: null;

				list.push(studentObj);
			}

			const response = await this.$apollo.mutate({
				mutation: gql`
					mutation($list: [StudentAddInputObjectGraphType]) {
						addManyStudent(list: $list) {
							id
						}
					}
				`,
				variables: {
					list: list
				}
			});
			if (response.data) {
				this.$store.commit('setLoading', false);
				this.closeModalAddStudentSim();
			}
		},
		updatePagination(page) {
			this.currentPage = page === 0 ? 1 : page;
		},
		updateSearchValue: _.debounce(function(input) {
			// reset the pagination as we can have more than one page
			// with the searched value
			this.currentPage = 1;
			this.searchValue = input.trim();
		}, 500),
		getWhereClause() {
			const filters = [];
			const where = this.simulation
				? `(RoleId == "7" && Students.Count(it.simulationId ==${this.simulation.id} && it.IsSimulationCompleted != "true") == 0)`
				: '';
			const nameSearch = this.searchValue
				? `${getNameAndEmailFilter(this.searchValue, '', 'emailAddress')}`
				: '';

			const source = 'Source = "' + this.activeFilter + '"';

			filters.push(where);
			filters.push(nameSearch);
			filters.push(source);

			return filters.filter(el => el).join('&&');
		},
		setActiveFilterChecks(filterType) {
			if (filterType !== studentsSources.UMS) {
				this.isFPactiveFilter = true;
				this.isUMSactiveFilter = false;
			} else {
				this.isFPactiveFilter = false;
				this.isUMSactiveFilter = true;
			}
		},
		setActiveFilter(filterType) {
			if (this.activeFilter === filterType) {
				return;
			}

			this.activeFilter = filterType;
			this.setActiveFilterChecks(filterType);
		},
		setFilter(filterType) {
			this.setActiveFilter(filterType);
			this.isPreviewVisible = false;
			this.isRequestInProgress = true;
		}
	},
	apollo: {
		students: {
			query: gql`
				query users($where: String!, $skip: Int!, $take: Int!) {
					users: users(where: $where, skip: $skip, take: $take) {
						id
						firstName
						lastName
						emailAddress
						simulations: students(where: "simulationId != null") {
							sessionName: simulation {
								name: sessionName
							}
							studentId: id
							simId: simulationId
						}
					}
					usersCount: getCountUser(where: $where)
				}
			`,
			variables() {
				return {
					where: this.getWhereClause(),
					skip: this.studentsPerPage * (this.currentPage - 1),
					take: this.studentsPerPage
				};
			},
			skip() {
				return !this.show;
			},
			update(response) {
				const { users, usersCount } = response;
				this.studentsCount = usersCount || 1;

				users.map(user => {
					user.simulations = _.uniqBy(user.simulations, 'simId');
				});

				return users;
			},
			fetchPolicy: 'no-cache'
		}
	}
};
</script>

<style lang="scss" scoped>
.add-student-sim-modal {
	position: fixed;
	// TODO: ::v-deep need to be removed
	::v-deep > .modal {
		height: 720px;
		width: 600px;
		display: flex;
		flex-direction: column;
	}
	.modal-header {
		.close-button {
			height: 38px;
			width: 38px;
		}
		.title-wrapper {
			display: inline-block;
			margin: 16px 0;
			width: 100%;
			text-align: center;
			vertical-align: middle;
			.modal-title {
				width: 80%;
				text-transform: capitalize;
				margin: auto;
			}
		}
	}

	.bottom-modal,
	.pagination-wrapper,
	.search-wrapper {
		border-top: 1px solid $button-border;
		height: 76px;
		display: flex;
		justify-content: center;
		align-items: center;

		button {
			margin: 16px 0;
		}
	}

	.pagination-wrapper,
	.search-wrapper {
		height: auto;
	}

	.search-wrapper,
	.sim-info-text {
		padding: 16px;
	}

	.sim-info-text,
	.students-container {
		border-top: 1px solid $button-border;
	}

	.toggle-button-wrapper {
		padding: 12px;
		border-top: 1px solid $button-border;

		.btn-group {
			width: 60%;
			margin: 0 auto;
		}
	}
}
::v-deep.modal-wrapper {
	overflow: hidden !important;
}
</style>
